import {
  CONTENT_HOUR_SLOT_HEADERS,
  slotCountsFromHours,
} from './contentHourBuckets';

export type EngagementCsvInput = {
  rangeLabel: string;
  days: number | null;
  countryCode: string;
  timezone: string;
  startDate?: string;
  endDate?: string;
  kpis: { label: string; value: number | null; delta: number | null }[];
  monetisationKpis: { label: string; value: number | null; delta: number | null }[];
  contentByType: { label: string; count: number; percentage: number; hourCounts?: number[] }[];
  engagementByType: {
    label: string;
    views: number;
    likes: number;
    comments: number;
    shares: number | null;
    engagementRate: number | null;
    hourCounts?: number[];
  }[];
  monetisationPeriods: string[];
  monetisationSeries: { name: string; values: number[] }[];
  communityPeriods: string[];
  communitySeries: { name: string; values: number[] }[];
  referrals: {
    successful: number | null;
    invitations: number | null;
    conversionRate: number | null;
  };
  topPaid: { title: string; creator: string; purchases: number }[];
  topPerforming: { title: string; type: string; creator: string; views: number }[];
};

function csvEscape(value: string | number | null | undefined): string {
  if (value == null) return '';
  const text = String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function section(
  title: string,
  headers: string[],
  rows: (string | number | null | undefined)[][],
): string[] {
  const lines = [csvEscape(title), headers.map(csvEscape).join(',')];
  for (const row of rows) {
    lines.push(row.map(csvEscape).join(','));
  }
  lines.push('');
  return lines;
}

export function buildEngagementDashboardCsv(input: EngagementCsvInput): string {
  const lines: string[] = [
    ...section(
      'Filters',
      ['range', 'days', 'eventLocation', 'timezone', 'startDate', 'endDate'],
      [
        [
          input.rangeLabel,
          input.days == null ? 'all' : input.days,
          input.countryCode || 'All',
          input.timezone,
          input.startDate ?? '',
          input.endDate ?? '',
        ],
      ],
    ),
    ...section(
      'KPIs',
      ['label', 'value', 'deltaPercent'],
      input.kpis.map((kpi) => [kpi.label, kpi.value, kpi.delta]),
    ),
    ...section(
      'Monetisation KPIs',
      ['label', 'value', 'deltaPercent'],
      input.monetisationKpis.map((kpi) => [kpi.label, kpi.value, kpi.delta]),
    ),
    ...section(
      'Content Overview',
      ['type', 'count', 'percentage'],
      input.contentByType.map((row) => [row.label, row.count, row.percentage]),
    ),
    ...section(
      'Content published by time of day',
      ['type', ...CONTENT_HOUR_SLOT_HEADERS],
      input.contentByType.map((row) => [row.label, ...slotCountsFromHours(row.hourCounts)]),
    ),
    ...section(
      'Engagement by Content Type',
      ['type', 'views', 'likes', 'comments', 'shares', 'engagementRate'],
      input.engagementByType.map((row) => [
        row.label,
        row.views,
        row.likes,
        row.comments,
        row.shares,
        row.engagementRate,
      ]),
    ),
    ...section(
      'Engagement by content type time of day',
      ['type', ...CONTENT_HOUR_SLOT_HEADERS],
      input.engagementByType.map((row) => [row.label, ...slotCountsFromHours(row.hourCounts)]),
    ),
    ...section(
      'Monetisation Timeseries',
      ['period', ...input.monetisationSeries.map((s) => s.name)],
      input.monetisationPeriods.map((period, index) => [
        period,
        ...input.monetisationSeries.map((s) => s.values[index] ?? 0),
      ]),
    ),
    ...section(
      'Community Timeseries',
      ['period', ...input.communitySeries.map((s) => s.name)],
      input.communityPeriods.map((period, index) => [
        period,
        ...input.communitySeries.map((s) => s.values[index] ?? 0),
      ]),
    ),
    ...section(
      'Referrals',
      ['successful', 'invitations', 'conversionRate'],
      [
        [
          input.referrals.successful,
          input.referrals.invitations,
          input.referrals.conversionRate,
        ],
      ],
    ),
    ...section(
      'Top Paid Posts',
      ['title', 'creator', 'purchases'],
      input.topPaid.map((row) => [row.title, row.creator, row.purchases]),
    ),
    ...section(
      'Top Performing Content',
      ['title', 'type', 'creator', 'views'],
      input.topPerforming.map((row) => [row.title, row.type, row.creator, row.views]),
    ),
  ];
  return lines.join('\n');
}

export function engagementCsvFilename(days: number | null): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const window = days == null ? 'all' : `${days}d`;
  return `engagement-dashboard-${window}-${stamp}.csv`;
}

export function downloadTextFile(
  filename: string,
  contents: string,
  mime = 'text/csv;charset=utf-8',
) {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
